/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useStatus } from '@/hooks/use-status'

import {
  SettingsForm,
  SettingsSwitchContent,
  SettingsSwitchItem
} from '../components/settings-form-layout'
import { SettingsPageFormActions } from '../components/settings-page-context'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

type Values = {
  enabled: boolean
  minQuota: number
  maxQuota: number
}

export function CheckinSettingsSection({
  defaultValues
}: {
  defaultValues: {
    enabled: boolean
    minQuota: number
    maxQuota: number
  }
}) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()
  const { status } = useStatus()
  const schema = useMemo(
    () =>
      z
        .object({
          enabled: z.boolean(),
          minQuota: z.coerce.number().min(0.1),
          maxQuota: z.coerce.number().min(0.1)
        })
        .superRefine((values, ctx) => {
          if (values.maxQuota < values.minQuota) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['maxQuota'],
              message: t('Maximum reward must be at least the minimum reward')
            })
          }

          for (const [field, value] of Object.entries({
            minQuota: values.minQuota,
            maxQuota: values.maxQuota
          })) {
            if (
              Math.abs(value * 10 - Math.round(value * 10)) > Number.EPSILON
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: t('Rewards must use 0.1 increments')
              })
            }
          }
        }),
    [t]
  )

  const quotaPerUnit = Number(status?.quota_per_unit) || 500000
  const quotaDisplayType = String(status?.quota_display_type || 'USD')
  const displayRate =
    quotaDisplayType === 'CNY'
      ? Number(status?.usd_exchange_rate) || 1
      : quotaDisplayType === 'CUSTOM'
        ? Number(status?.custom_currency_exchange_rate) || 1
        : 1
  const quotaMultiplier =
    quotaDisplayType === 'TOKENS' ? 1 : quotaPerUnit / displayRate
  const isLegacyZeroToOne =
    defaultValues.minQuota === 0 && defaultValues.maxQuota === 1

  const displayDefaults = useMemo(() => {
    const toDisplayReward = (quota: number) =>
      Math.round((quota / quotaMultiplier) * 10) / 10

    return {
      enabled: defaultValues.enabled,
      minQuota: isLegacyZeroToOne
        ? 0.1
        : toDisplayReward(defaultValues.minQuota),
      maxQuota: isLegacyZeroToOne ? 1 : toDisplayReward(defaultValues.maxQuota)
    }
  }, [defaultValues, isLegacyZeroToOne, quotaMultiplier])

  const form = useForm<Values>({
    resolver: zodResolver(schema) as unknown as Resolver<Values>,
    defaultValues: displayDefaults
  })

  const { isDirty, isSubmitting } = form.formState
  const enabled = form.watch('enabled')

  useEffect(() => {
    if (!form.formState.isDirty) {
      form.reset(displayDefaults)
    }
  }, [displayDefaults, form])

  async function onSubmit(values: Values) {
    const updates: Array<{ key: string; value: string }> = []

    if (values.enabled !== displayDefaults.enabled) {
      updates.push({
        key: 'checkin_setting.enabled',
        value: String(values.enabled)
      })
    }

    const toStoredQuota = (reward: number) =>
      Math.round(reward * quotaMultiplier)
    const minQuota = toStoredQuota(values.minQuota)
    const maxQuota = toStoredQuota(values.maxQuota)
    const stepQuota = toStoredQuota(0.1)

    const hasRangeChange =
      isLegacyZeroToOne ||
      values.minQuota !== displayDefaults.minQuota ||
      values.maxQuota !== displayDefaults.maxQuota

    if (hasRangeChange) {
      updates.push({
        key: 'checkin_setting.min_quota',
        value: String(minQuota)
      })
      updates.push({
        key: 'checkin_setting.max_quota',
        value: String(maxQuota)
      })
    }

    updates.push({
      key: 'checkin_setting.step_quota',
      value: String(stepQuota)
    })

    if (updates.length === 0) {
      toast.info(t('No changes to save'))
      return
    }

    for (const update of updates) {
      await updateOption.mutateAsync(update)
    }

    form.reset(values)
  }

  return (
    <SettingsSection title={t('Check-in Settings')}>
      <Form {...form}>
        <SettingsForm onSubmit={form.handleSubmit(onSubmit)} autoComplete="off">
          <SettingsPageFormActions
            onSave={form.handleSubmit(onSubmit)}
            isSaving={updateOption.isPending || isSubmitting}
            isSaveDisabled={!isDirty}
            saveLabel="Save check-in settings"
          />
          <FormField
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <SettingsSwitchItem>
                <SettingsSwitchContent>
                  <FormLabel>{t('Enable check-in feature')}</FormLabel>
                  <FormDescription>
                    {t(
                      'Allow users to check in daily for random quota rewards'
                    )}
                  </FormDescription>
                </SettingsSwitchContent>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={updateOption.isPending || isSubmitting}
                  />
                </FormControl>
              </SettingsSwitchItem>
            )}
          />

          {enabled && (
            <div className="grid gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="minQuota"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('Minimum check-in quota')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0.1}
                        step={0.1}
                        placeholder="0.1"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('Rewards start at 0.1 and use 0.1 increments.')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxQuota"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('Maximum check-in quota')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0.1}
                        step={0.1}
                        placeholder="1.0"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('The maximum reward is included in the random draw.')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </SettingsForm>
      </Form>
    </SettingsSection>
  )
}
